import React, { useState, useEffect } from 'react';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw,
  RotateCcw, ShieldCheck, Database, History, FileText, ArrowRight,
  ChevronDown, ChevronUp, Lock, Check, XCircle, Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const DataImportCenterPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();

  const [activeTab, setActiveTab] = useState<'import' | 'history'>('import');

  // Step state: 1: Upload, 2: Preview & Validation, 3: Reconciliation
  const [step, setStep] = useState<number>(1);

  // Form state
  const [selectedSession, setSelectedSession] = useState('3');
  const [selectedMonth, setSelectedMonth] = useState('September');
  const [updateExisting, setUpdateExisting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Analysis / Preview state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reconciliationResult, setReconciliationResult] = useState<any | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  // History state
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [rollingBackId, setRollingBackId] = useState<number | null>(null);
  const [historyMessage, setHistoryMessage] = useState('');

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchWithAuth('/api/import/history');
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          setHistoryList(json.data || []);
        }
      }
    } catch (e) {
      console.error('Error fetching import history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setAnalysisResult(null);
      setReconciliationResult(null);
      setErrorMessage('');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a valid .xlsx or .csv file to import.');
      return;
    }

    setAnalyzing(true);
    setErrorMessage('');

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const result = event.target?.result;
          let payload: any = {
            file_name: selectedFile.name,
            academic_session_id: parseInt(selectedSession, 10),
            month: selectedMonth
          };

          if (selectedFile.name.endsWith('.csv')) {
            payload.file_text = result as string;
          } else {
            const base64String = (result as string).split(',')[1] || (result as string);
            payload.file_base64 = base64String;
          }

          const res = await fetchWithAuth('/api/import/analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          const json = await res.json();
          if (json.success && json.data) {
            setAnalysisResult(json.data);
            setStep(2);
          } else {
            setErrorMessage(json.message || 'File validation failed.');
          }
        } catch (err: any) {
          setErrorMessage('Error sending file for validation: ' + err.message);
        } finally {
          setAnalyzing(false);
        }
      };

      if (selectedFile.name.endsWith('.csv')) {
        reader.readAsText(selectedFile);
      } else {
        reader.readAsDataURL(selectedFile);
      }
    } catch (e: any) {
      setErrorMessage('Error reading file: ' + e.message);
      setAnalyzing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!analysisResult?.preview_token) return;

    setConfirming(true);
    setErrorMessage('');

    try {
      const res = await fetchWithAuth('/api/import/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preview_token: analysisResult.preview_token,
          update_existing: updateExisting
        })
      });

      const json = await res.json();
      if (json.success && json.data) {
        setReconciliationResult(json.data);
        setStep(3);
      } else {
        setErrorMessage(json.message || 'Failed to commit import.');
      }
    } catch (e: any) {
      setErrorMessage('Error committing import: ' + e.message);
    } finally {
      setConfirming(false);
    }
  };

  const handleRollback = async (importId: number) => {
    if (!window.confirm(`Are you sure you want to rollback Import #${importId}? This will safely delete only the records created by this specific import.`)) {
      return;
    }

    setRollingBackId(importId);
    setHistoryMessage('');
    try {
      const res = await fetchWithAuth(`/api/import/rollback/${importId}`, {
        method: 'POST'
      });
      const json = await res.json();
      if (json.success) {
        setHistoryMessage(`Import #${importId} rolled back successfully. Removed ${json.data?.marks_removed} assessment marks.`);
        fetchHistory();
      } else {
        setHistoryMessage('Rollback failed: ' + json.message);
      }
    } catch (e: any) {
      setHistoryMessage('Error rolling back import: ' + e.message);
    } finally {
      setRollingBackId(null);
    }
  };

  const handleResetImport = () => {
    setStep(1);
    setSelectedFile(null);
    setAnalysisResult(null);
    setReconciliationResult(null);
    setErrorMessage('');
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Official Government Header */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-[#0F2C59] rounded-xl border border-blue-200 shadow-inner">
            <Database className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">
                Official Data Import &amp; Reconciliation Center
              </h1>
              <span className="bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded uppercase border border-amber-300">
                Transactional Safety
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Secure workflow: Upload → Analyze → Validate → Preview → Confirm → Reconcile with 100% duplicate protection and rollback safety.
            </p>
          </div>
        </div>

        {/* Tab Toggle */}
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg border border-slate-200">
          <button
            onClick={() => setActiveTab('import')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${
              activeTab === 'import' ? 'bg-white text-[#0F2C59] shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            New Data Import
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-3 py-1.5 text-xs font-bold rounded-md transition ${
              activeTab === 'history' ? 'bg-white text-[#0F2C59] shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Import History &amp; Rollback
          </button>
        </div>
      </div>

      {/* Alerts */}
      {errorMessage && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-900 rounded-xl text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span className="font-semibold">{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage('')} className="font-bold text-rose-700 hover:underline">Dismiss</button>
        </div>
      )}

      {historyMessage && (
        <div className="p-4 bg-blue-50 border border-blue-200 text-blue-900 rounded-xl text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="font-semibold">{historyMessage}</span>
          </div>
          <button onClick={() => setHistoryMessage('')} className="font-bold text-blue-700 hover:underline">Dismiss</button>
        </div>
      )}

      {/* TAB 1: NEW DATA IMPORT WORKFLOW */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between max-w-2xl mx-auto text-xs font-bold">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#0F2C59]' : 'text-slate-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step >= 1 ? 'bg-[#0F2C59] text-white' : 'bg-slate-200 text-slate-600'}`}>
                  1
                </div>
                <span>Upload &amp; Analyze</span>
              </div>
              <div className={`h-0.5 flex-1 mx-4 ${step >= 2 ? 'bg-[#0F2C59]' : 'bg-slate-200'}`} />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#0F2C59]' : 'text-slate-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step >= 2 ? 'bg-[#0F2C59] text-white' : 'bg-slate-200 text-slate-600'}`}>
                  2
                </div>
                <span>Validation Preview</span>
              </div>
              <div className={`h-0.5 flex-1 mx-4 ${step >= 3 ? 'bg-[#0F2C59]' : 'bg-slate-200'}`} />
              <div className={`flex items-center gap-2 ${step >= 3 ? 'text-emerald-700' : 'text-slate-400'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step >= 3 ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                  3
                </div>
                <span>Reconciliation Report</span>
              </div>
            </div>
          </div>

          {/* STEP 1: FILE UPLOAD FORM */}
          {step === 1 && (
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Target Academic Year
                  </label>
                  <select
                    value={selectedSession}
                    onChange={e => setSelectedSession(e.target.value)}
                    className="w-full p-2.5 text-xs border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-semibold"
                  >
                    <option value="3">2026-27 (Current Active Session)</option>
                    <option value="2">2025-26 (Historical)</option>
                    <option value="1">2024-25 (Historical)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Target Assessment Month
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(e.target.value)}
                    className="w-full p-2.5 text-xs border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-semibold"
                  >
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="August">August</option>
                    <option value="July">July</option>
                    <option value="May">May</option>
                  </select>
                </div>
              </div>

              {/* Upload Dropzone */}
              <div className="border-2 border-dashed border-slate-300 hover:border-[#0F2C59] rounded-xl p-8 text-center transition bg-slate-50/50">
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-slate-800">
                  {selectedFile ? selectedFile.name : 'Select or Drag Excel (.xlsx) / CSV (.csv) File'}
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                  File should contain columns: <code>School Code, SRN, Student Name, Class, Section, Subject, Marks</code>.
                </p>

                <div className="mt-4">
                  <input
                    type="file"
                    id="import-file-input"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="import-file-input"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-xs font-bold cursor-pointer hover:bg-slate-50 shadow-sm"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    <span>Browse Files</span>
                  </label>
                </div>
              </div>

              {/* Analyze Button */}
              <div className="flex justify-end pt-2">
                <button
                  disabled={!selectedFile || analyzing}
                  onClick={handleAnalyze}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0F2C59] text-white rounded-lg text-xs font-bold hover:bg-[#1E40AF] transition disabled:opacity-50 shadow"
                >
                  {analyzing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Validating Rows...</span>
                    </>
                  ) : (
                    <>
                      <span>Analyze &amp; Validate File</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: VALIDATION PREVIEW */}
          {step === 2 && analysisResult && (
            <div className="space-y-6">
              {/* File & Validation Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* File Summary */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider border-b pb-2">
                    <FileText className="w-4 h-4 text-[#0F2C59]" />
                    <span>File Analysis Summary</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div>File Name: <strong className="text-slate-900">{analysisResult.file_summary?.file_name}</strong></div>
                    <div>Total Data Rows: <strong className="text-slate-900">{analysisResult.file_summary?.total_rows}</strong></div>
                    <div>Detected Academic Year: <strong className="text-slate-900">{analysisResult.file_summary?.detected_academic_year}</strong></div>
                    <div>Detected Month: <strong className="text-slate-900">{analysisResult.file_summary?.detected_month}</strong></div>
                    <div>Detected Schools: <strong className="text-slate-900">{analysisResult.file_summary?.detected_schools_count}</strong></div>
                    <div>Detected Subjects: <strong className="text-slate-900">{analysisResult.file_summary?.detected_subjects_count}</strong></div>
                  </div>
                </div>

                {/* Validation Summary */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-wider border-b pb-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>Validation &amp; Integrity Breakdown</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                      <div className="text-[10px] text-emerald-700 font-bold uppercase">New Records</div>
                      <div className="text-lg font-black text-emerald-900">{analysisResult.validation_summary?.new_records}</div>
                    </div>
                    <div className="p-2 bg-blue-50 rounded border border-blue-200">
                      <div className="text-[10px] text-blue-700 font-bold uppercase">Already in DB</div>
                      <div className="text-lg font-black text-blue-900">{analysisResult.validation_summary?.existing_records}</div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded border border-amber-200">
                      <div className="text-[10px] text-amber-700 font-bold uppercase">Duplicates</div>
                      <div className="text-lg font-black text-amber-900">{analysisResult.validation_summary?.duplicate_in_file}</div>
                    </div>
                    <div className="p-2 bg-rose-50 rounded border border-rose-200">
                      <div className="text-[10px] text-rose-700 font-bold uppercase">Conflicts</div>
                      <div className="text-lg font-black text-rose-900">{analysisResult.validation_summary?.conflicts}</div>
                    </div>
                    <div className="p-2 bg-rose-50 rounded border border-rose-200">
                      <div className="text-[10px] text-rose-700 font-bold uppercase">Invalid Rows</div>
                      <div className="text-lg font-black text-rose-900">{analysisResult.validation_summary?.invalid_records}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded border border-slate-200">
                      <div className="text-[10px] text-slate-600 font-bold uppercase">Total Valid</div>
                      <div className="text-lg font-black text-slate-900">{analysisResult.validation_summary?.valid_records}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sample Preview Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                  <div className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Interactive Validation Preview (First 50 Rows)
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Row-by-row integrity verification
                  </span>
                </div>

                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-left text-xs text-slate-700">
                    <thead className="bg-slate-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider sticky top-0 border-b">
                      <tr>
                        <th className="p-2.5">Row</th>
                        <th className="p-2.5">Status</th>
                        <th className="p-2.5">School Code</th>
                        <th className="p-2.5">School Name</th>
                        <th className="p-2.5">SRN</th>
                        <th className="p-2.5">Student Name</th>
                        <th className="p-2.5">Class</th>
                        <th className="p-2.5">Subject</th>
                        <th className="p-2.5 text-center">Marks</th>
                        <th className="p-2.5">Validation Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-sans">
                      {analysisResult.sample_preview?.map((r: any, idx: number) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-mono text-slate-400">#{r.row}</td>
                          <td className="p-2.5">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              r.classification === 'NEW'
                                ? 'bg-emerald-100 text-emerald-800'
                                : r.classification === 'ALREADY EXISTS'
                                ? 'bg-blue-100 text-blue-800'
                                : r.classification === 'DUPLICATE IN FILE'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}>
                              {r.classification}
                            </span>
                          </td>
                          <td className="p-2.5 font-mono text-[#0F2C59] font-bold">{r.school_code}</td>
                          <td className="p-2.5 font-medium max-w-xs truncate">{r.school_name}</td>
                          <td className="p-2.5 font-mono text-slate-500">{r.srn}</td>
                          <td className="p-2.5 font-bold text-slate-900">{r.student_name}</td>
                          <td className="p-2.5">Class {r.class_id === 1 ? '10' : '12'}</td>
                          <td className="p-2.5 font-medium">{r.subject_name}</td>
                          <td className="p-2.5 text-center font-bold">
                            {r.is_absent === 1 ? 'AB' : r.obtained_marks}
                          </td>
                          <td className="p-2.5 text-slate-500 text-[11px] max-w-xs truncate" title={r.notes}>
                            {r.notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Import Options & Action */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={e => setUpdateExisting(e.target.checked)}
                    className="w-4 h-4 text-[#0F2C59] rounded"
                  />
                  <span>Update existing database marks if values differ (Review Required / Changed Marks)</span>
                </label>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetImport}
                    className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50"
                  >
                    Cancel &amp; Re-Upload
                  </button>

                  <button
                    disabled={confirming || analysisResult.validation_summary?.valid_records === 0}
                    onClick={handleConfirmImport}
                    className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold shadow disabled:opacity-50 transition"
                  >
                    {confirming ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Creating Safety Backup &amp; Committing...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>CONFIRM IMPORT ({analysisResult.validation_summary?.new_records} New)</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: POST-IMPORT RECONCILIATION REPORT */}
          {step === 3 && reconciliationResult && (
            <div className="space-y-6">
              <div className="bg-emerald-50 border border-emerald-300 p-6 rounded-xl text-emerald-950 space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                  <div>
                    <h3 className="text-base font-bold text-emerald-900">
                      Import #{reconciliationResult.import_id} Committed Successfully
                    </h3>
                    <p className="text-xs text-emerald-800">
                      Automated database safety backup created prior to write at: <code>{reconciliationResult.backup_file}</code>
                    </p>
                  </div>
                </div>

                {/* Counts Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div className="p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="text-[10px] uppercase font-bold text-slate-500">Marks Inserted</div>
                    <div className="text-2xl font-black text-emerald-900 mt-0.5">{reconciliationResult.counts?.marks_inserted}</div>
                  </div>
                  <div className="p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="text-[10px] uppercase font-bold text-slate-500">Marks Updated</div>
                    <div className="text-2xl font-black text-blue-900 mt-0.5">{reconciliationResult.counts?.marks_updated}</div>
                  </div>
                  <div className="p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="text-[10px] uppercase font-bold text-slate-500">New Students Created</div>
                    <div className="text-2xl font-black text-indigo-900 mt-0.5">{reconciliationResult.counts?.new_students_created}</div>
                  </div>
                  <div className="p-3 bg-white/80 rounded-lg border border-emerald-200">
                    <div className="text-[10px] uppercase font-bold text-slate-500">Rows Skipped</div>
                    <div className="text-2xl font-black text-slate-700 mt-0.5">{reconciliationResult.counts?.marks_skipped}</div>
                  </div>
                </div>
              </div>

              {/* Block-wise Breakdown */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Block-Wise Import Reconciliation
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {Object.entries(reconciliationResult.block_breakdown || {}).map(([blk, stats]: any) => (
                    <div key={blk} className="p-3 bg-slate-50 rounded-lg border text-xs">
                      <div className="font-bold text-slate-900">{blk}</div>
                      <div className="text-[11px] text-emerald-700 mt-1">Inserted: <strong>{stats.inserted}</strong></div>
                      <div className="text-[11px] text-blue-700">Updated: <strong>{stats.updated}</strong></div>
                      <div className="text-[11px] text-slate-500">Skipped: <strong>{stats.skipped}</strong></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={handleResetImport}
                  className="px-6 py-2.5 bg-[#0F2C59] text-white text-xs font-bold rounded-lg hover:bg-[#1E40AF] transition"
                >
                  Start Another Import
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: IMPORT HISTORY & SAFE ROLLBACK */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-5">
          <div className="flex justify-between items-center pb-2 border-b">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Permanent Import Ledger &amp; Rollback History</h3>
              <p className="text-xs text-slate-500">Complete immutable log of all batch assessment imports with rollback safety.</p>
            </div>
            <button
              onClick={fetchHistory}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingHistory ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider border-b">
                <tr>
                  <th className="p-3">Import ID</th>
                  <th className="p-3">File Name</th>
                  <th className="p-3">Date &amp; Time</th>
                  <th className="p-3">User</th>
                  <th className="p-3">Session / Month</th>
                  <th className="p-3 text-center">Rows</th>
                  <th className="p-3 text-center">Inserted</th>
                  <th className="p-3 text-center">Updated</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loadingHistory ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-slate-500">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-400" />
                      Loading import history...
                    </td>
                  </tr>
                ) : historyList.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-slate-500">
                      No import history records found.
                    </td>
                  </tr>
                ) : (
                  historyList.map(h => (
                    <tr key={h.import_id} className="hover:bg-slate-50/80">
                      <td className="p-3 font-mono font-bold text-[#0F2C59]">#{h.import_id}</td>
                      <td className="p-3 font-semibold text-slate-900 max-w-xs truncate">{h.file_name}</td>
                      <td className="p-3 text-slate-500">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="p-3 font-medium">{h.username} ({h.role})</td>
                      <td className="p-3 font-medium">{h.academic_session} / {h.month || '—'}</td>
                      <td className="p-3 text-center font-mono">{h.total_rows}</td>
                      <td className="p-3 text-center font-bold text-emerald-700">{h.imported_count || 0}</td>
                      <td className="p-3 text-center font-bold text-blue-700">{h.updated_count || 0}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          h.status === 'IMPORTED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : h.status === 'ROLLED_BACK'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {h.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        {h.is_rolled_back === 1 ? (
                          <span className="text-[11px] text-slate-400 font-semibold italic">Rolled Back</span>
                        ) : (
                          <button
                            disabled={rollingBackId === h.import_id}
                            onClick={() => handleRollback(h.import_id)}
                            className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 rounded text-[11px] font-semibold transition disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            <RotateCcw className="w-3 h-3" />
                            <span>{rollingBackId === h.import_id ? 'Rolling Back...' : 'Rollback Import'}</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataImportCenterPage;
